import type { NamedSet } from 'zustand/middleware';

import type { ConversationEvent } from '../../repositories/conversationRepository';
import type { ZustandStore } from '../store.type';

export type RequestType = {
	amount: string | number;
	attachments: [];
	businessId: string;
	cardType: string;
	coding: any;
	currency: string;
	fields: any;
	isArchived: boolean;
	lastEdit: string;
	profileId: string;
	requester: {
		email: string;
		firstName: string;
		lastName: string;
		properties: any;
		teamApproverEmail: string;
	};
	authPaddingPercentage: number;
	requestId: string;
	state: string;
	teamId: string;
	title: string;
};

type RequestFields = {
	id: string;
	label: string;
	valueFormat: string;
	required: boolean;
	description: string;
};

type CardType = {
	feature: string;
	cardType: string;
	label: string;
};

export type CodingList = {
	ID?: string;
	code?: string;
	name?: string;
	group?: string;
	ordering?: string;
	description?: string;
	targets?: string[];
	enabled?: boolean;
	entries?: Entry[];
	listId?: string;
};

type Entry = {
	ID?: string;
	path?: string;
	entryId?: string;
	entryLabel?: string;
};

type RequestState = {
	request: {
		requests: RequestType[];
		cardTypes?: CardType[];
		purchaseRequest: RequestType;
		archivedRequests: RequestType[];
		requestFields: RequestFields[];
		lists: CodingList[];
		errors: any;
		allEvents: ConversationEvent[]; /// used to concatenate events to be shown in ui
	};
};

type RequestActions = {
	setCardTypes: (cardTypes: RequestState['request']['cardTypes']) => void;
	setActiveLists: (lists: RequestState['request']['lists']) => void;
	setPurchaseRequest: (purchaseRequest: RequestState['request']['purchaseRequest']) => void;
	setRequests: (requests: RequestState['request']['requests']) => void;
	setRequestFields: (requestFields: RequestState['request']['requestFields']) => void;
	setArchivedRequests: (requests: RequestState['request']['archivedRequests']) => void;
	setRequestErrors: (errors: RequestState['request']['errors']) => void;
	setAllEvents: (allEvents: RequestState['request']['allEvents']) => void;
};

export type RequestSlice = RequestState & RequestActions;

const requestInitialState: RequestState = {
	request: {
		cardTypes: [],
		lists: [],
		purchaseRequest: {
			amount: 0,
			attachments: [],
			businessId: '',
			cardType: '',
			coding: {},
			currency: '',
			fields: {},
			isArchived: false,
			lastEdit: '',
			profileId: '',
			requester: { email: '', firstName: '', lastName: '', properties: {}, teamApproverEmail: '' },
			authPaddingPercentage: 0,
			requestId: '',
			state: '',
			teamId: '',
			title: '',
		},
		requests: [],
		archivedRequests: [],
		requestFields: [],
		errors: {},
		allEvents: [],
	},
};

export function requestSlice(set: NamedSet<ZustandStore>): RequestSlice {
	return {
		...requestInitialState,
		setCardTypes(cardTypes: RequestState['request']['cardTypes']): void {
			set(
				(state: RequestState) => {
					state.request.cardTypes = cardTypes || [];
					return state;
				},
				false,
				'[setCardTypes]',
			);
		},
		setActiveLists(lists: RequestState['request']['lists']): void {
			set(
				(state: RequestState) => {
					state.request.lists = lists || [];
					return state;
				},
				false,
				'[setActiveLists]',
			);
		},
		setPurchaseRequest(purchaseRequest: RequestState['request']['purchaseRequest']): void {
			set(
				(state: RequestState) => {
					state.request.purchaseRequest = { ...state.request.purchaseRequest, ...purchaseRequest };
					return state;
				},
				false,
				'[setPurchaseRequest]',
			);
		},
		setRequests(requests: RequestState['request']['requests']): void {
			set(
				(state: RequestState) => {
					state.request.requests = requests;
					return state;
				},
				false,
				'[setRequests]',
			);
		},
		setArchivedRequests(archivedRequests: RequestState['request']['archivedRequests']): void {
			set(
				(state: RequestState) => {
					state.request.archivedRequests = archivedRequests;
					return state;
				},
				false,
				'[setArchivedRequests]',
			);
		},
		setRequestFields(requestFields: RequestState['request']['requestFields']): void {
			set(
				(state: RequestState) => {
					state.request.requestFields = requestFields;
					return state;
				},
				false,
				'[setRequestFields]',
			);
		},
		setRequestErrors(errors: RequestState['request']['errors']): void {
			set(
				(state: RequestState) => {
					state.request.errors = errors;
					return state;
				},
				false,
				'[setRequestErrors]',
			);
		},
		setAllEvents(allEvents: RequestState['request']['allEvents']): void {
			set(
				(state: RequestState) => {
					state.request.allEvents = allEvents;
					return state;
				},
				false,
				'[setAllEvents]',
			);
		},
	};
}
